import { CustomMenu, useMenuState, MenuItemInternalLink, Button } from "@octopusdeploy/design-system-components";
import type { DeploymentStepResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import { useEffect } from "react";
import type { Location } from "react-router-dom";
import { addSampleStepsToProcessResource, bashStep, powerShellStep } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorActions } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { useProjectContext } from "~/areas/projects/context";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PermissionCheck } from "~/components/PermissionCheck";
interface SampleProcessButtonProps {
    href: LinkHref;
    location: Location;
}
enum SampleStepType {
    Bash,
    PowerShell
}
function generateSampleStep(stepType: SampleStepType): DeploymentStepResource {
    switch (stepType) {
        case SampleStepType.Bash:
            return bashStep();
        case SampleStepType.PowerShell:
            return powerShellStep();
        default:
            exhaustiveCheck(stepType, "Unsupported step type");
    }
}
function SampleProcessButton({ href, location }: SampleProcessButtonProps) {
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const navigation = useSpaceAwareNavigation();
    const processContext = useProcessContext();
    const { state: { gitRef }, } = useProjectContext();
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const saveSampleStep = async (stepType: SampleStepType) => {
        const sampleStep = generateSampleStep(stepType);
        const sampleProcess = addSampleStepsToProcessResource(processContext.selectors.getProcessResource(), [sampleStep]);
        await processContext.actions.saveOnServer(sampleProcess, (errors) => {
            errorActions.setErrors(errors, processContext.selectors);
            warningActions.clearWarnings();
        }, () => {
            errorActions.clearErrors();
            warningActions.clearWarnings();
        }, gitRef?.CanonicalName);
    };
    useEffect(() => {
        const loadSampleProcessAutomatically = new URLSearchParams(location.search).get("loadSampleProcess");
        if (loadSampleProcessAutomatically) {
            saveSampleStep(loadSampleProcessAutomatically === "ps" ? SampleStepType.PowerShell : SampleStepType.Bash);
            navigation.redirect(href);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (<PermissionCheck permission={Permission.ProcessEdit}>
            <Button importance="secondary" label="Use sample" onClick={openMenu} {...buttonAriaAttributes}/>
            <CustomMenu accessibleName={"Deploy release"} menuState={menuState}>
                <MenuItemInternalLink path={href} label={"Hello world (PowerShell)"} onClick={() => saveSampleStep(SampleStepType.PowerShell)}></MenuItemInternalLink>
                <MenuItemInternalLink path={href} label={"Hello world (Bash)"} onClick={() => saveSampleStep(SampleStepType.Bash)}></MenuItemInternalLink>
            </CustomMenu>
        </PermissionCheck>);
}
export default SampleProcessButton;
